import type {
  DihedralProfile,
  FailedTorsionRow,
  FittedTorsionRow,
  FragmentSummary,
} from "@/api/affdo";
import { useFragmentDihedrals, useFragmentProfiles, useRunFragments } from "@/api/affdo";
import { resolveFragmentId } from "@/components/runs/fragment-selection";
import type { TorsionPick } from "@/components/runs/torsion-selection";
import { orderedTorsionIds, resolveTorsionId } from "@/components/runs/torsion-selection";

/**
 * A run's two torsion AXES — which fragment, which torsion — plus the three
 * live reads they resolve against (VIS/V9).
 *
 * **Why this is a module of its own.** The axes used to be resolved inside
 * `RunTorsionPlots`, which was fine while everything driven by them was also
 * inside it. VIS/V9 moves the per-angle structure OUT of the plots pane and
 * into the workspace's rail, so the curve and the structure are now two
 * siblings that must agree about which torsion is on screen — and the scan
 * geometry the rail draws is addressed by `(runId, fragmentId, torsionId)`,
 * the resolved pair. Two components each resolving their own copy is how the
 * two halves start disagreeing; the same argument that lifted the raw PICKS to
 * the workspace in flowstack#991 lifts the RESOLUTION now.
 *
 * It is also why this is a plain module rather than something exported from
 * `RunTorsionPlots`: that file reaches recharts through
 * `DihedralRMSDGrid`, and the workspace holds it behind `React.lazy`
 * precisely so ~380 KB of chart code does not land on every route
 * (`chart-lazy-boundary.test.ts`). Importing the hook from there would put it
 * back.
 *
 * The resolution RULES are not restated here — `resolveFragmentId` and
 * `resolveTorsionId` are the ones U5.1 shipped, and this composes them in the
 * one order that makes them nest correctly: the fragment axis settles first,
 * then the torsion axis resolves INSIDE whatever it settled on. A `torsion_id`
 * is unique only within one fragment's result, so that order is the whole
 * reason a `TorsionPick` carries the fragment it was made in.
 */
export interface RunTorsionAxes {
  /** Every fragment this run has published, in the server's order. */
  rows: FragmentSummary[];
  /** The fragment on screen, resolved against {@link rows}. */
  fragmentId: string | null;
  /** This fragment's torsions, worst-fitting first. */
  torsionIds: string[];
  /** The torsion on screen, resolved within {@link fragmentId}. */
  torsionId: string | null;
  /** The resolved torsion's profile — the curve's half of the scan join. */
  profile: DihedralProfile | undefined;
  fitted: FittedTorsionRow[] | undefined;
  failed: FailedTorsionRow[] | undefined;
  profiles: DihedralProfile[] | undefined;
  /** The fragment INDEX is still in flight. */
  fragmentsLoading: boolean;
  /** The fragment index failed — a different claim from "no fragments yet"
   * (flowstack#923), so the two are separate fields rather than one. */
  fragmentsError: unknown;
  /** Either per-fragment read is in flight. Collapsed deliberately: the grid
   * draws RMSD panels from one and curves from the other, so treating them
   * separately renders complete-looking panels with their curves missing. */
  detailLoading: boolean;
  /** Whichever per-fragment read failed, dihedrals first, else `null`. Both
   * produce the same sentence ("could not load fragment X"), because from the
   * reader's side they are one failure. */
  detailError: unknown;
}

/**
 * Resolve both axes for one run.
 *
 * `enabled: false` parks every read without unmounting the caller — the run
 * workspace passes it whenever the main area is not the torsion mode, so
 * opening a run on the DAG costs the three AFFDO requests it always did (none)
 * rather than acquiring them because the rail now draws a structure.
 */
export function useRunTorsionAxes({
  runId,
  progressKey,
  fragmentPick,
  torsionPick,
  enabled = true,
}: {
  runId: string;
  /** The run view's DAG-poll fingerprint; see `useRunFragments`. */
  progressKey?: string;
  /** The user's RAW fragment pick, unresolved. */
  fragmentPick: string | null;
  /** The user's RAW torsion pick — id AND the fragment it was made in. */
  torsionPick: TorsionPick | null;
  enabled?: boolean;
}): RunTorsionAxes {
  const fragments = useRunFragments(runId, { enabled, refreshKey: progressKey });
  const rows = fragments.data?.fragments ?? [];
  const fragmentId = resolveFragmentId(rows, fragmentPick);

  const dihedrals = useFragmentDihedrals(runId, fragmentId ?? "", {
    enabled: enabled && fragmentId !== null,
    refreshKey: progressKey,
  });
  const profiles = useFragmentProfiles(runId, fragmentId ?? "", {
    enabled: enabled && fragmentId !== null,
    refreshKey: progressKey,
  });

  const torsionIds = orderedTorsionIds(dihedrals.data?.fitted, dihedrals.data?.failed);
  const torsionId = resolveTorsionId(torsionIds, torsionPick, fragmentId);

  return {
    rows,
    fragmentId,
    torsionIds,
    torsionId,
    profile: profiles.data?.profiles?.find((p) => p.torsion_id === torsionId),
    fitted: dihedrals.data?.fitted,
    failed: dihedrals.data?.failed,
    profiles: profiles.data?.profiles,
    fragmentsLoading: fragments.isLoading,
    fragmentsError: fragments.isError ? fragments.error : null,
    detailLoading: dihedrals.isLoading || profiles.isLoading,
    detailError: dihedrals.isError ? dihedrals.error : profiles.isError ? profiles.error : null,
  };
}
